import { createAuth0User, getTenantById, updateTenant, sendPasswordReset } from "../api/tenantApi";

// Auth0 password policy: >=8 chars incl. upper, lower, digit, special (!@#$%^&*).
// The prospect never sees this — they set their own via the activation email.
export const randomPassword = () => {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghijkmnpqrstuvwxyz";
  const digit = "23456789";
  const special = "!@#$%^&*";
  const all = upper + lower + digit + special;
  const pick = (s) => s[Math.floor(Math.random() * s.length)];
  let pw = pick(upper) + pick(lower) + pick(digit) + pick(special);
  for (let i = 0; i < 14; i++) pw += pick(all);
  return pw.split("").sort(() => Math.random() - 0.5).join("");
};

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Invite a single user into a tenant:
 *  1. create the Auth0 login account (random password),
 *  2. add them to the tenant's email list,
 *  3. send an activation / set-password email.
 * Returns nothing; throws on the account-creation step (the important one).
 */
export const inviteUser = async (tenant, { email, name, roleId = null, isAdmin = false }) => {
  const tenantId = tenant?.id;
  if (!tenantId) throw new Error("Missing tenant");
  const clean = String(email || "").trim();
  if (!EMAIL_RE.test(clean)) throw new Error("Invalid email");

  // 1. Create the account. If it already exists, that's fine — we'll just
  //    (re)send the activation email below, so an invite is idempotent.
  try {
    await createAuth0User({
      email: clean,
      name: name?.trim() || clean.split("@")[0],
      password: randomPassword(),
      connection: "Username-Password-Authentication",
      user_metadata: { tenantId, roleId: roleId || null, isAdmin: !!isAdmin },
      app_metadata: { tenantId, roleId: roleId || null },
      verify_email: false,
    });
  } catch (e) {
    const detail = e?.response?.data?.details || e?.response?.data || {};
    const msg = `${detail.message || detail.error || e?.message || ""}`.toLowerCase();
    const status = detail.statusCode || e?.response?.status;
    const alreadyExists = status === 409 || msg.includes("already exists");
    if (!alreadyExists) throw e; // genuine failure
    // else fall through — account exists, just resend the activation email.
  }

  // 2. Keep the tenant's email list in sync (non-fatal).
  try {
    const tRes = await getTenantById(tenantId);
    const tData = tRes?.data || {};
    if (!tData?.emailList?.includes(clean)) {
      await updateTenant(tenantId, { emailList: tData.emailList ? [...tData.emailList, clean] : [clean] });
    }
  } catch (e) {
    console.warn("emailList sync failed for", clean, e?.message);
  }

  // 3. Send the activation e-mail (branded, via Resend) so they set their password.
  await sendPasswordReset(clean, { name: name?.trim(), tenantName: tenant?.name });
};

// Parse a pasted block of emails (commas / spaces / newlines), de-duplicated.
export const parseEmails = (text) => {
  const parts = String(text || "").split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
  return [...new Set(parts)];
};
